import { action, observable, set } from 'mobx';
import { FBSchemaProps } from '..';
import FBMPIProcedureState from '../FBMPIProcedure/FBMPIProcedure.state';
import { FBDeviationEditorConfig, FBDeviationStepValue, isDeviationProcedureStep } from './FBDeviationProcedure.types';

export default class FBDeviationProcedureState {
  // Set right before the field editor opens for an MPI section (Add Field, or a component's Edit);
  // consumed by FBEditorActions.handleAdd so the field is saved into the deviation, not the deviation's own schema
  public static onSave?: (item: FBSchemaProps) => void;

  // MARK: @observables
  @observable public editorConfig: FBDeviationEditorConfig = {};
  @observable public deviatedMpiIds = new Set<string>();

  // keyed `${mpiId}::${stepId}` → per-step state
  public readonly stepStates = new Map<string, FBMPIProcedureState>();

  // MARK: @constructor
  public constructor (editorConfig?: FBDeviationEditorConfig) {
    this.editorConfig = editorConfig ?? {};
    Object.entries(this.editorConfig).forEach(([mpiId, steps]) => {
      Object.entries(steps).forEach(([stepId, stepData]) => {
        if (!isDeviationProcedureStep(stepData)) { return; }
        this.stepStates.set(`${mpiId}::${stepId}`, new FBMPIProcedureState(stepData));
      });
    });
  }

  // MARK: @actions
  @action public initDeviatedMpiIds = (ids: string[]): void => {
    ids.forEach((id) => this.deviatedMpiIds.add(id));
  };

  @action public updateStep = (mpiId: string, stepId: string, value: FBDeviationStepValue): void => {
    this.deviatedMpiIds.add(mpiId);
    set(this, 'editorConfig', {
      ...this.editorConfig,
      [mpiId]: {
        ...this.editorConfig[mpiId],
        [stepId]: { ...this.editorConfig[mpiId]?.[stepId], ...value },
      },
    });
  };

  @action public addComponent = (mpiId: string, item: FBSchemaProps): void => {
    // New components go to the end of the section; they can be moved from there
    const lastIndex = Math.max(-1, ...Object.values(this.editorConfig[mpiId] ?? {}).map((it) => Number(it.schemaItem?.index) || 0));
    const schemaItem = { ...item, index: lastIndex + 1 };
    set(this, 'editorConfig', {
      ...this.editorConfig,
      [mpiId]: {
        ...this.editorConfig[mpiId],
        [item.name as string]: { type: item.type, label: item.label as string, schemaItem, added: true },
      },
    });
    this.deviatedMpiIds.add(mpiId);
  };

  // Edited in the field editor: keep the key (the entered value is stored under it) and the position
  @action public editComponent = (mpiId: string, stepId: string, item: FBSchemaProps): void => {
    const step = this.editorConfig[mpiId]?.[stepId];
    if (!step?.schemaItem) { return; }
    const schemaItem = { ...step.schemaItem, ...item, name: step.schemaItem.name, index: step.schemaItem.index };
    this.updateStep(mpiId, stepId, { ...step, type: item.type, label: item.label as string, schemaItem } as FBDeviationStepValue);
  };

  // Position is the schemaItem's index (fractions allowed, so a component can slot between two others)
  @action public setComponentIndex = (mpiId: string, stepId: string, index: number): void => {
    const step = this.editorConfig[mpiId]?.[stepId];
    if (!step?.schemaItem) { return; }
    this.updateStep(mpiId, stepId, { schemaItem: { ...step.schemaItem, index } } as FBDeviationStepValue);
  };

  @action public removeComponent = (mpiId: string, stepId: string): void => {
    const { [stepId]: _removed, ...rest } = this.editorConfig[mpiId] ?? {};
    set(this, 'editorConfig', { ...this.editorConfig, [mpiId]: rest });
  };

  // MARK: @helpers
  public getStepState = (mpiId: string, stepId: string): FBMPIProcedureState | undefined =>
    this.stepStates.get(`${mpiId}::${stepId}`);
}
